(() => {
  /* ================================
     NordTablo – Variant Media Filter + Stage Bars (V3.2)
     V3.1 Stable + Focal Patch (Zoom Sync + Flickity Recenter)
  ================================ */

  // ─── Konstanten ───────────────────────────────────────────────────────────
  const MAIN_PRODUCT_SELECTOR   = '.shopify-section--main-product';
  const SELECTOR_MEDIA_LIST     = '.product__media-list';
  const SELECTOR_MEDIA_ITEMS    = '.product__media-item[data-media-id]';
  const SELECTOR_THUMB_INNER    = '.product__thumbnail-list-inner';
  const SELECTOR_DOTS_NAV       = '.product__media-nav:not(.media-modal .product__media-nav) .dots-nav';
  const SELECTOR_DOTS_ITEM      = '.product__media-nav:not(.media-modal .product__media-nav) .dots-nav__item';
  const SELECTOR_MEDIA          = '.product__media';
  const SELECTOR_VARIANT_INPUT  = 'input[name="id"]';
  const SELECTOR_CAROUSEL       = 'flickity-carousel';
  const SELECTOR_ZOOM_BTN       = '.product__zoom-button';
  const SELECTOR_MEDIA_ITEM_IMG = '.product__media-item[data-media-id]';

  const BARS_MIN               = 1000;
  const BARS_MAX               = 1199;
  const MAX_BOOTSTRAP_ATTEMPTS = 20;

  // ─── Root Guard ───────────────────────────────────────────────────────────
  const root = document.querySelector(MAIN_PRODUCT_SELECTOR);
  if (!root) return;

  function markMediaReady() {
    root.classList.add('nt-media-ready');
  }

  // ─── Event-Listener refs (für teardown) ───────────────────────────────────
  let _resizeHandler          = null;
  let _pageHideHandler        = null;
  let _pageShowHandler        = null;
  let _zoomClickHandler       = null;
  let _zoomCloseHandler       = null;
  let _variantChangedHandler  = null;
  let _flickityReadyObserver  = null;
  let _flickityReadyMediaList = null;

  let _variantInput   = null;
  let _variantHandler = null;

  // ─── Gemeinsamer State ────────────────────────────────────────────────────
  let lastVariantMediaId = null;
  let retryTimer         = null;
  let barsTimer          = null;
  let switchLockTimer    = null;
  let rafId              = 0;
  let barsRaf            = 0;
  let recenterRaf        = 0;
  let bootstrapAttempts  = 0;
  let observers          = [];
  let isApplying         = false;

  let _flickityCache = null;
  let _flickityEl    = null;

  // ─── Observer Helpers ─────────────────────────────────────────────────────
  function cleanupObservers() {
    observers.forEach(obs => obs.disconnect());
    observers = [];
  }

  function addObserver(element, callback, options) {
    if (!element) return;
    const obs = new MutationObserver(callback);
    obs.observe(element, options);
    observers.push(obs);
  }

  function disconnectFlickityReadyObserver() {
    if (_flickityReadyObserver) {
      try { _flickityReadyObserver.disconnect(); } catch (_) {}
      _flickityReadyObserver = null;
    }
    _flickityReadyMediaList = null;
  }

  function getVariantMediaIdFromEvent(e) {
    const detail = e && e.detail ? e.detail : null;
    const variant =
      (detail && (detail.variant || detail.productVariant || detail.currentVariant)) || null;

    const mediaId =
      (variant && variant.featured_media && variant.featured_media.id) ||
      (detail && detail.featured_media && detail.featured_media.id) ||
      (detail && detail.media && detail.media.id) ||
      null;

    return mediaId ? String(mediaId) : null;
  }

  function startVariantSwitchLock(duration = 280) {
    clearTimeout(switchLockTimer);
    root.classList.add('nt-variant-switching');

    switchLockTimer = setTimeout(() => {
      root.classList.remove('nt-variant-switching');
    }, duration);
  }

  function runImmediateReadyPass() {
    if (!root) return;

    if (!lastVariantMediaId) {
      bootstrapVariantId();
    }

    let didFilterChange = false;

    isApplying = true;
    try {
      didFilterChange = applyMediaFilter();
      applyThumbnailMark();
      syncStageBars();
    } finally {
      isApplying = false;
    }

    markMediaReady();

    if (didFilterChange) {
      scheduleRecenter();
    }
  }

  // ─── Flickity Cache ───────────────────────────────────────────────────────
  function getFlickity() {
    const mediaList = root.querySelector(`${SELECTOR_MEDIA_LIST}.flickity-enabled`);
    if (!mediaList || !window.Flickity) {
      _flickityCache = null;
      _flickityEl    = null;
      return null;
    }
    if (_flickityCache && _flickityEl === mediaList) {
      return _flickityCache;
    }
    _flickityEl    = mediaList;
    _flickityCache = window.Flickity.data(mediaList) || null;
    return _flickityCache;
  }

  // ─── Carousel Element (Focal Promise API) ─────────────────────────────────
  function getCarouselEl() {
    return (
      root.querySelector(`${SELECTOR_CAROUSEL}.product__media-list`) ||
      root.querySelector(SELECTOR_CAROUSEL)
    );
  }

  // ─── Mockup-Erkennung ─────────────────────────────────────────────────────
  function isMockupElement(el) {
    if (!el) return false;
    const img = el.querySelector('img');
    if (!img) return false;

    const src     = img.getAttribute('src') || '';
    const srcset  = img.getAttribute('srcset') || '';
    const dataSrc = img.getAttribute('data-src') || '';
    const dataSet = img.getAttribute('data-srcset') || '';

    const haystack = `${src} ${srcset} ${dataSrc} ${dataSet}`.toLowerCase();
    return haystack.includes('mockup');
  }

  // ─── Sichtbare Items ──────────────────────────────────────────────────────
  function getVisibleItems() {
    return Array.from(root.querySelectorAll(SELECTOR_MEDIA_ITEMS))
      .filter(el => !el.classList.contains('is-filtered'));
  }

  // ─── Aktives Medium ───────────────────────────────────────────────────────
  function getActiveMedia() {
    const flkty = getFlickity();
    if (flkty && flkty.selectedElement) {
      const closest = flkty.selectedElement.closest(SELECTOR_MEDIA_ITEMS);
      if (closest) return closest;
    }

    return (
      root.querySelector(`${SELECTOR_MEDIA_ITEMS}.is-selected, ${SELECTOR_MEDIA_ITEMS}.is-active`) ||
      root.querySelector(`${SELECTOR_MEDIA_ITEMS}:not(.is-filtered)`) ||
      root.querySelector(SELECTOR_MEDIA_ITEMS)
    );
  }

  // ─── Bootstrap VariantId ──────────────────────────────────────────────────
  function bootstrapVariantId() {
    if (lastVariantMediaId) return true;

    const flkty = getFlickity();
    if (flkty) {
      const cell = flkty.cells.find(c => c.element && !isMockupElement(c.element));
      if (cell) {
        lastVariantMediaId = cell.element.getAttribute('data-media-id');
        return true;
      }
    }

    const selected = root.querySelector(`${SELECTOR_MEDIA_ITEMS}.is-selected, ${SELECTOR_MEDIA_ITEMS}.is-active`);
    if (selected && !isMockupElement(selected)) {
      lastVariantMediaId = selected.getAttribute('data-media-id');
      return true;
    }

    const firstVariant = Array.from(root.querySelectorAll(SELECTOR_MEDIA_ITEMS))
      .find(item => !isMockupElement(item));
    if (firstVariant) {
      lastVariantMediaId = firstVariant.getAttribute('data-media-id');
      return true;
    }

    return false;
  }

  // ─── Mobile Dots Sync ─────────────────────────────────────────────────────
  function syncMobileBar() {
    if (window.innerWidth >= BARS_MIN) return;

    const dotsNav = root.querySelector(SELECTOR_DOTS_NAV);
    if (!dotsNav) return;

    const visibleItems = getVisibleItems();
    if (!visibleItems.length) return;

    const visibleCount = visibleItems.length;
    const existingDots = Array.from(dotsNav.querySelectorAll(SELECTOR_DOTS_ITEM));

    if (existingDots.length !== visibleCount) {
      const template = existingDots[0];
      if (!template) return;

      dotsNav.innerHTML = '';
      for (let i = 0; i < visibleCount; i++) {
        const dot = template.cloneNode(true);
        dot.classList.remove('is-selected');
        dot.removeAttribute('aria-current');
        dot.setAttribute('data-index', i);

        dot.addEventListener('click', () => {
          const flkty   = getFlickity();
          const visible = getVisibleItems();
          const target  = visible[i];
          if (!target || !flkty) return;

          const id  = target.getAttribute('data-media-id');
          const abs = flkty.cells.findIndex(
            c => c.element?.getAttribute('data-media-id') === id
          );

          if (abs >= 0) flkty.select(abs, false, true);
        });

        dotsNav.appendChild(dot);
      }
    }

    const active      = getActiveMedia();
    const activeIndex = visibleItems.indexOf(active);
    const dots        = Array.from(dotsNav.querySelectorAll(SELECTOR_DOTS_ITEM));

    dots.forEach((dot, i) => {
      const isActive = i === activeIndex;
      dot.classList.toggle('is-selected', isActive);
      if (isActive) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  // ─── Media Filter ─────────────────────────────────────────────────────────
  function applyMediaFilter() {
    if (!lastVariantMediaId) {
      if (!bootstrapVariantId()) return false;
    }

    const items = Array.from(root.querySelectorAll(SELECTOR_MEDIA_ITEMS));
    let visibilityChanged = false;

    for (const item of items) {
      const id   = item.getAttribute('data-media-id');
      const keep = (id === lastVariantMediaId) || isMockupElement(item);

      const nextFiltered = !keep;
      const nextAria     = keep ? 'false' : 'true';

      if (item.classList.contains('is-filtered') !== nextFiltered) {
        item.classList.toggle('is-filtered', nextFiltered);
        visibilityChanged = true;
      }

      if (item.getAttribute('aria-hidden') !== nextAria) {
        item.setAttribute('aria-hidden', nextAria);
      }
    }

    const active = getActiveMedia();
    if (active && active.classList.contains('is-filtered')) {
      const flkty = getFlickity();
      if (flkty) {
        const idx = flkty.cells.findIndex(
          c => c.element?.getAttribute('data-media-id') === lastVariantMediaId
        );
        if (idx >= 0) flkty.select(idx, false, true);
      }
    }

    const flkty = getFlickity();
    if (visibilityChanged && flkty) {
      flkty.reloadCells();
      flkty.resize();
      flkty.reposition();
    }

    syncMobileBar();
    return visibilityChanged;
  }

  // ─── Thumbnail Mark ───────────────────────────────────────────────────────
  function applyThumbnailMark() {
    if (window.innerWidth < 750) return;
    if (!lastVariantMediaId) return;

    const thumbInner = root.querySelector(SELECTOR_THUMB_INNER);
    if (!thumbInner) return;

    const candidate = thumbInner.querySelector(
      `.product__thumbnail-item[data-media-id="${lastVariantMediaId}"]`
    );
    if (!candidate || isMockupElement(candidate)) return;

    const marked = thumbInner.querySelector('.nt-variant-thumb');
    if (marked !== candidate) {
      if (marked) marked.classList.remove('nt-variant-thumb');
      candidate.classList.add('nt-variant-thumb');
    }
  }

  // ─── Stage Bars: DOM ──────────────────────────────────────────────────────
  const barsInRange = () => window.innerWidth >= BARS_MIN && window.innerWidth <= BARS_MAX;

  function removeBars() {
    const bar = root.querySelector('.nt-stage-bars');
    if (bar) bar.remove();
  }

  function ensureBars(count) {
    const media = root.querySelector(SELECTOR_MEDIA);
    if (!media) return null;

    let bar = root.querySelector('.nt-stage-bars');
    if (!bar) {
      bar = document.createElement('div');
      bar.className = 'nt-stage-bars';
      media.appendChild(bar);
    }

    const current = bar.querySelectorAll('.nt-stage-bars__seg').length;
    if (current !== count) {
      bar.innerHTML = '';
      for (let i = 0; i < count; i++) {
        const seg = document.createElement('button');
        seg.type = 'button';
        seg.className = 'nt-stage-bars__seg';
        seg.setAttribute('aria-label', `Bild ${i + 1}`);

        seg.addEventListener('click', () => {
          const flkty   = getFlickity();
          const visible = getVisibleItems();
          const target  = visible[i];
          if (!target || !flkty) return;

          const id  = target.getAttribute('data-media-id');
          const abs = flkty.cells.findIndex(
            c => c.element?.getAttribute('data-media-id') === id
          );

          if (abs >= 0) flkty.select(abs, false, true);
        });

        bar.appendChild(seg);
      }
    }

    return bar;
  }

  // ─── Stage Bars: Sync ─────────────────────────────────────────────────────
  function syncStageBars() {
    if (!barsInRange()) {
      removeBars();
      return;
    }

    const visible = getVisibleItems();
    if (visible.length <= 1) {
      removeBars();
      return;
    }

    const bar = ensureBars(visible.length);
    if (!bar) return;

    const active    = getActiveMedia();
    const activeIdx = Math.max(0, visible.indexOf(active));
    const segs      = Array.from(bar.querySelectorAll('.nt-stage-bars__seg'));

    segs.forEach((seg, i) => seg.classList.toggle('is-active', i === activeIdx));
  }

  // ─── scheduleRecenter ─────────────────────────────────────────────────────
  async function scheduleRecenter() {
    const carouselEl = getCarouselEl();
    if (!carouselEl || !carouselEl.flickityInstance) return;

    cancelAnimationFrame(recenterRaf);
    recenterRaf = requestAnimationFrame(async () => {
      try {
        const flkty = await carouselEl.flickityInstance;
        if (!flkty) return;
        flkty.resize?.();
        flkty.reposition?.();
      } catch (_) {}
    });
  }

  // ─── Media-Item in Flickity selektieren ───────────────────────────────────
  async function selectMediaItem(mediaItemEl, instant = true) {
    if (!mediaItemEl) return;
    if (mediaItemEl.classList.contains('is-filtered')) return;

    const carouselEl = getCarouselEl();
    if (!carouselEl || !carouselEl.flickityInstance) return;

    try {
      const flkty = await carouselEl.flickityInstance;
      if (!flkty) return;

      try {
        flkty.selectCell(mediaItemEl, true, instant);
      } catch (_) {
        const cells = (flkty.cells || []).map(c => c.element);
        const idx = cells.indexOf(mediaItemEl);
        if (idx >= 0) flkty.select(idx, instant, true);
      }

      scheduleRecenter();
    } catch (_) {}
  }

  // ─── Image-Load Recenter binden ───────────────────────────────────────────
  function bindSelectedImageLoadRecenter() {
    const carouselEl = getCarouselEl();
    if (!carouselEl || carouselEl.__ntImgLoadBound) return;
    carouselEl.__ntImgLoadBound = true;

    // Flickity-Init exakt abfangen
    const mediaList = root.querySelector(SELECTOR_MEDIA_LIST);
    if (mediaList) {
      const runIfFlickityReady = () => {
        if (mediaList.classList.contains('flickity-enabled')) {
          disconnectFlickityReadyObserver();
          runImmediateReadyPass();
        }
      };

      if (!root.classList.contains('nt-media-ready')) {
        runIfFlickityReady();

        if (!root.classList.contains('nt-media-ready') && !_flickityReadyObserver) {
          _flickityReadyMediaList = mediaList;
          _flickityReadyObserver = new MutationObserver((mutations) => {
            for (const m of mutations) {
              if (m.type === 'attributes' && m.attributeName === 'class') {
                runIfFlickityReady();
                return;
              }
            }
          });

          _flickityReadyObserver.observe(mediaList, {
            attributes: true,
            attributeFilter: ['class']
          });
        }
      }
    }

    const hook = async () => {
      try {
        const flkty = await carouselEl.flickityInstance;
        const selected = flkty?.selectedElement;
        if (!selected) {
          scheduleRecenter();
          return;
        }

        const img = selected.querySelector('img');
        if (!img || img.complete) {
          scheduleRecenter();
          return;
        }

        img.addEventListener('load',  () => scheduleRecenter(), { once: true });
        img.addEventListener('error', () => scheduleRecenter(), { once: true });
      } catch (_) {}
    };

    carouselEl.addEventListener('flickity:ready', hook);
    carouselEl.addEventListener('flickity:slide-changed', hook);
    carouselEl.addEventListener('flickity:slide-settled', hook);

    hook();
  }

  // ─── scheduleUpdate ───────────────────────────────────────────────────────
  function scheduleUpdate(delay = 0) {
    clearTimeout(retryTimer);
    retryTimer = setTimeout(() => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const active = getActiveMedia();
        if (!active) return;

        const id = active.getAttribute('data-media-id');

        if (!isMockupElement(active)) {
          if (!lastVariantMediaId || !active.classList.contains('is-filtered')) {
            lastVariantMediaId = id;
          }
        }

        let didFilterChange = false;

        isApplying = true;
        try {
          didFilterChange = applyMediaFilter();
          applyThumbnailMark();
          syncStageBars();
        } finally {
          isApplying = false;
        }

        markMediaReady();

        if (didFilterChange) {
          scheduleRecenter();
        }
      });
    }, delay);
  }

  // ─── scheduleBarSync ──────────────────────────────────────────────────────
  function scheduleBarSync(delay = 0) {
    cancelAnimationFrame(barsRaf);
    clearTimeout(barsTimer);

    if (delay <= 0) {
      barsRaf = requestAnimationFrame(syncStageBars);
    } else {
      barsTimer = setTimeout(() => {
        barsRaf = requestAnimationFrame(syncStageBars);
      }, delay);
    }
  }

  // ─── Flickity Events ──────────────────────────────────────────────────────
  function bindFlickityBarsEvents() {
    const flkty = getFlickity();
    if (!flkty || flkty.__ntBarsBound) return;
    flkty.__ntBarsBound = true;

    const selectHandler = () => scheduleBarSync(0);
    const settleHandler = () => scheduleBarSync(0);

    flkty.__ntSelectHandler = selectHandler;
    flkty.__ntSettleHandler = settleHandler;

    flkty.on('select', selectHandler);
    flkty.on('settle', settleHandler);
  }

  // ─── Observers Setup ──────────────────────────────────────────────────────
  function setupObservers() {
    cleanupObservers();

    const mediaList  = root.querySelector(SELECTOR_MEDIA_LIST);
    const thumbInner = root.querySelector(SELECTOR_THUMB_INNER);
    const dotsNav    = root.querySelector(SELECTOR_DOTS_NAV);

    const guardedUpdate = () => {
      if (isApplying) return;
      scheduleUpdate(0);
    };

    if (mediaList) {
      addObserver(mediaList, guardedUpdate, {
        subtree: true,
        attributes: true,
        childList: true,
        attributeFilter: ['class', 'aria-current', 'aria-hidden', 'data-media-id']
      });
    }

    if (thumbInner) {
      addObserver(thumbInner, guardedUpdate, {
        subtree: true,
        attributes: true,
        childList: true,
        attributeFilter: ['class', 'aria-current', 'data-media-id']
      });
    }

    if (dotsNav) {
      addObserver(dotsNav, guardedUpdate, {
        subtree: true,
        attributes: true,
        childList: true,
        attributeFilter: ['class', 'aria-current']
      });
    }
  }

  // ─── Variant Input Binding ────────────────────────────────────────────────
  function bindVariantChange() {
    const variantInput = root.querySelector(SELECTOR_VARIANT_INPUT);
    if (variantInput && !variantInput.__ntBound) {
      variantInput.__ntBound = true;

      const handler = () => {
        startVariantSwitchLock(280);
        scheduleUpdate(120);
        scheduleBarSync(160);
        scheduleBarSync(420);
        scheduleBarSync(800);
      };

      _variantInput   = variantInput;
      _variantHandler = handler;

      variantInput.addEventListener('change', handler);
    }

    if (_variantChangedHandler) return;

    _variantChangedHandler = (e) => {
      if (e.target && e.target !== document && !root.contains(e.target)) return;

      const nextMediaId = getVariantMediaIdFromEvent(e);
      if (nextMediaId) {
        lastVariantMediaId = nextMediaId;
      }

      startVariantSwitchLock(280);

      scheduleUpdate(0);
      scheduleUpdate(70);
      scheduleUpdate(160);

      scheduleBarSync(0);
      scheduleBarSync(140);
      scheduleBarSync(360);
    };

    document.addEventListener('variant:changed', _variantChangedHandler, true);
  }

  // ─── Aggressive Bootstrap ────────────────────────────────────────────────
  function aggressiveBootstrap() {
    bootstrapAttempts++;
    if (!lastVariantMediaId) bootstrapVariantId();

    if (lastVariantMediaId) {
      let didFilterChange = false;

      isApplying = true;
      try {
        didFilterChange = applyMediaFilter();
        applyThumbnailMark();
        syncStageBars();
      } finally {
        isApplying = false;
      }

      markMediaReady();

      if (didFilterChange) {
        scheduleRecenter();
      }
    }

    if (!lastVariantMediaId && bootstrapAttempts < MAX_BOOTSTRAP_ATTEMPTS) {
      scheduleUpdate(100);
    }
  }

  // ─── Stage Bars Init-Polling ──────────────────────────────────────────────
  function initStageBarPolling() {
    let tries = 0;
    const t = setInterval(() => {
      tries++;
      bindFlickityBarsEvents();
      scheduleBarSync(0);
      if (tries > 25) clearInterval(t);
    }, 120);
  }

  // ─── Teardown ─────────────────────────────────────────────────────────────
  function teardown() {
    if (_resizeHandler) {
      window.removeEventListener('resize', _resizeHandler);
      _resizeHandler = null;
    }

    if (_pageHideHandler) {
      window.removeEventListener('pagehide', _pageHideHandler);
      _pageHideHandler = null;
    }
    if (_pageShowHandler) {
      window.removeEventListener('pageshow', _pageShowHandler);
      _pageShowHandler = null;
    }

    if (_zoomClickHandler) {
      document.removeEventListener('click', _zoomClickHandler, true);
      _zoomClickHandler = null;
    }
    if (_zoomCloseHandler) {
      document.removeEventListener('openable-element:close', _zoomCloseHandler, true);
      _zoomCloseHandler = null;
    }
    if (_variantChangedHandler) {
      document.removeEventListener('variant:changed', _variantChangedHandler, true);
      _variantChangedHandler = null;
    }

    if (_variantInput && _variantHandler) {
      _variantInput.removeEventListener('change', _variantHandler);
      _variantInput.__ntBound = false;
      _variantInput   = null;
      _variantHandler = null;
    }

    if (_flickityCache) {
      if (_flickityCache.__ntSelectHandler) {
        _flickityCache.off('select', _flickityCache.__ntSelectHandler);
        _flickityCache.__ntSelectHandler = null;
      }
      if (_flickityCache.__ntSettleHandler) {
        _flickityCache.off('settle', _flickityCache.__ntSettleHandler);
        _flickityCache.__ntSettleHandler = null;
      }
      _flickityCache.__ntBarsBound = false;
      _flickityCache = null;
      _flickityEl    = null;
    }

    disconnectFlickityReadyObserver();
    cleanupObservers();
    clearTimeout(retryTimer);
    clearTimeout(barsTimer);
    clearTimeout(switchLockTimer);
    root.classList.remove('nt-variant-switching');
    cancelAnimationFrame(rafId);
    cancelAnimationFrame(barsRaf);
    cancelAnimationFrame(recenterRaf);
  }

  // ─── Doppelte Init verhindern ─────────────────────────────────────────────
  if (root.__ntInit) {
    root.__ntInit.teardown();
  }
  root.__ntInit = { teardown };

  // ─── Resize Handler ───────────────────────────────────────────────────────
  _resizeHandler = () => {
    scheduleUpdate(80);
    scheduleRecenter();
  };
  window.addEventListener('resize', _resizeHandler, { passive: true });

  // ─── BFCache / Back Button Fix ────────────────────────────────────────────
  _pageHideHandler = (e) => {
    if (e && e.persisted) {
      clearTimeout(retryTimer);
      clearTimeout(barsTimer);
      clearTimeout(switchLockTimer);
      cancelAnimationFrame(rafId);
      cancelAnimationFrame(barsRaf);
      cancelAnimationFrame(recenterRaf);
      return;
    }
    teardown();
  };

  _pageShowHandler = () => {
    _flickityCache = null;
    _flickityEl    = null;

    setupObservers();
    bindVariantChange();
    bindFlickityBarsEvents();
    bindSelectedImageLoadRecenter();

    bootstrapAttempts = 0;

    let didFilterChange = false;

    isApplying = true;
    try {
      if (!lastVariantMediaId) bootstrapVariantId();
      didFilterChange = applyMediaFilter();
      applyThumbnailMark();
      syncStageBars();
    } finally {
      isApplying = false;
    }

    markMediaReady();

    if (didFilterChange) {
      scheduleRecenter();
    }

    scheduleUpdate(0);
    scheduleUpdate(120);
    scheduleUpdate(420);

    scheduleBarSync(0);
    scheduleBarSync(220);
    scheduleBarSync(520);

    scheduleRecenter();
  };

  window.addEventListener('pagehide', _pageHideHandler, { passive: true });
  window.addEventListener('pageshow', _pageShowHandler, { passive: true });

  // ─── Zoom-Click Handler (capture) ─────────────────────────────────────────
  _zoomClickHandler = async (e) => {
    const btn = e.target.closest(SELECTOR_ZOOM_BTN);
    if (!btn) return;
    if (!root.contains(btn)) return;

    const mediaItem = btn.closest(SELECTOR_MEDIA_ITEM_IMG);
    if (mediaItem) {
      await selectMediaItem(mediaItem, true);
    }
  };
  document.addEventListener('click', _zoomClickHandler, true);

  // ─── Zoom-Close Handler ───────────────────────────────────────────────────
  _zoomCloseHandler = async (e) => {
    const zoomEl = e.target;
    if (!(zoomEl instanceof HTMLElement)) return;
    if (zoomEl.tagName.toLowerCase() !== 'product-image-zoom') return;
    if (!root.contains(zoomEl)) return;

    let idx = null;
    try {
      const pswp = zoomEl.photoSwipeInstance;
      if (pswp) {
        if (typeof pswp.getCurrentIndex === 'function') idx = pswp.getCurrentIndex();
        else if (typeof pswp.currIndex === 'number') idx = pswp.currIndex;
      }
    } catch (_) {}

    const activeImages = Array.from(
      root.querySelectorAll('.product__media-item[data-media-type="image"]:not(.is-filtered)')
    );

    const fallback = root.querySelector('.product__media-item.is-selected') || activeImages[0] || null;
    const target   = (idx !== null && idx >= 0 && idx < activeImages.length)
      ? activeImages[idx]
      : fallback;

    if (target) {
      await selectMediaItem(target, true);
    } else {
      scheduleRecenter();
    }
  };
  document.addEventListener('openable-element:close', _zoomCloseHandler, true);

  // ─── Init ─────────────────────────────────────────────────────────────────
  aggressiveBootstrap();
  setupObservers();
  bindVariantChange();
  initStageBarPolling();
  bindSelectedImageLoadRecenter();

  scheduleUpdate(50);
  scheduleUpdate(150);
  scheduleUpdate(350);
  scheduleUpdate(650);
  scheduleUpdate(1000);
})();


(() => {
  /* =========================================================
     NT – Thumbnail Sharpness Patch (CSS/DOM-safe)
     ========================================================= */

  const MAIN = '.shopify-section--main-product';
  const THUMB_SCOPE = '.product__thumbnail-list, .product__thumbnail-list-inner, .product__thumbnail-item, .product__thumbnail';
  const DESKTOP_MQ = window.matchMedia('(min-width: 1000px)');

  const DESKTOP_SIZES =
    '(min-width: 1400px) 118px, (min-width: 1200px) 110px, (min-width: 1000px) 96px, 72px';

  let root = document.querySelector(MAIN);
  if (!root) return;

  let mo = null;
  let resizeTimer = null;
  let startObserverRetryTimer = null;

  function isInThumb(img) {
    return !!img.closest(THUMB_SCOPE);
  }

  function isZoomOrModal(img) {
    return !!img.closest('.media-modal, .pswp') || img.classList.contains('pswp__img');
  }

  function isResponsiveImg(img) {
    return img && img.tagName === 'IMG' && img.hasAttribute('srcset');
  }

  function upgradeOne(img) {
    if (!isResponsiveImg(img)) return;
    if (!isInThumb(img)) return;
    if (isZoomOrModal(img)) return;
    if (!DESKTOP_MQ.matches) return;

    const curSizes = (img.getAttribute('sizes') || '').trim();
    if (curSizes === DESKTOP_SIZES) return;

    img.setAttribute('sizes', DESKTOP_SIZES);

    const clone = img.cloneNode(true);
    clone.setAttribute('sizes', DESKTOP_SIZES);
    clone.dataset.ntThumbSharp = '1';

    img.replaceWith(clone);
  }

  function run() {
    const thumbRoot = root.querySelector('.product__thumbnail-list, .product__thumbnail-list-inner');
    if (!thumbRoot) return;
    thumbRoot.querySelectorAll('img[srcset]').forEach(upgradeOne);
  }

  function startObserver() {
    if (mo) mo.disconnect();
    if (startObserverRetryTimer) {
      clearInterval(startObserverRetryTimer);
      startObserverRetryTimer = null;
    }

    const tryBind = () => {
      const thumbRoot = root.querySelector('.product__thumbnail-list, .product__thumbnail-list-inner');
      if (!thumbRoot) return false;

      mo = new MutationObserver(() => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(run, 80);
      });

      mo.observe(thumbRoot, { childList: true, subtree: true });
      return true;
    };

    if (tryBind()) return;

    let tries = 0;
    startObserverRetryTimer = setInterval(() => {
      tries++;
      if (tryBind() || tries > 25) {
        clearInterval(startObserverRetryTimer);
        startObserverRetryTimer = null;
      }
    }, 120);
  }

  function onResize() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      run();
    }, 120);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      root = document.querySelector(MAIN);
      if (!root) return;
      run();
      startObserver();
      window.addEventListener('resize', onResize, { passive: true });
    }, { once: true });
  } else {
    run();
    startObserver();
    window.addEventListener('resize', onResize, { passive: true });
  }

  const existingInit = root.__ntInit;
  if (existingInit && typeof existingInit.teardown === 'function') {
    const prevTeardown = existingInit.teardown;
    existingInit.teardown = function () {
      try {
        if (mo) mo.disconnect();
        mo = null;
        if (startObserverRetryTimer) {
          clearInterval(startObserverRetryTimer);
          startObserverRetryTimer = null;
        }
        window.removeEventListener('resize', onResize);
        clearTimeout(resizeTimer);
      } catch (_) {}
      return prevTeardown.apply(this, arguments);
    };
  }
})();





/* ── Varianten: Zoll-Angabe entfernen ── */
(function () {
  'use strict';

  const strip = (s) => s.replace(/\s*\/.*$/, '').trim();

  const clean = (el) => {
    el.childNodes.forEach(node => {
      if (node.nodeType === Node.TEXT_NODE && node.textContent.includes('/')) {
        node.textContent = strip(node.textContent);
      }
    });
  };

  const run = () =>
    document.querySelectorAll('.combo-option__text, .select__selected-value').forEach(clean);

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', run)
    : run();

  document.addEventListener('variant:changed', () => {
    setTimeout(run, 0);
  });

  document.addEventListener('change', (e) => {
    const t = e.target;
    if (!t || !t.matches) return;
    if (
      t.matches('input[name="id"]') ||
      t.matches('select[name="id"]') ||
      t.matches('.combo-option__input') ||
      t.matches('.select__input')
    ) {
      setTimeout(run, 0);
    }
  }, true);

  function bindScopedObservers() {
    const scopes = document.querySelectorAll(
      '.product__variant-picker, .product__option, variant-picker, variant-radios, variant-select'
    );
    if (!scopes.length) return;

    const debouncedRun = (() => {
      let t = null;
      return () => {
        if (t) return;
        t = setTimeout(() => { t = null; run(); }, 50);
      };
    })();

    scopes.forEach((scope) => {
      if (scope.__ntZollObserved) return;
      scope.__ntZollObserved = true;
      try {
        new MutationObserver(debouncedRun).observe(scope, {
          childList: true,
          subtree: true,
        });
      } catch (_) {}
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindScopedObservers);
  } else {
    bindScopedObservers();
  }
})();




/* =========================================================
   MOBILE STAGE – ADAPTIVE HEIGHT (DELAYED PREMIUM EXPAND)
   ========================================================= */
(function () {
  if (window.innerWidth > 999) return;

  var SELECTORS = {
    root: '.shopify-section--main-product',
    wrapper: '.product__media-list-wrapper',
    mediaList: '.product__media-list',
    selected: '.product__media-item.is-selected',
    imgs: '.product__media-item img'
  };

  var CONFIG = {
    defaultVhFactor: 0.72,
    defaultVwFactor: 1.25,

    mockupExtraPx: 60,
    landscapeExtraPx: 40,

    mockupMaxVh: 0.65,
    landscapeMaxVh: 0.60,

    mockupMinVh: 0.38,
    landscapeMinVh: 0.35,

    changeDelay: 240,
    variantDelay: 280,
    resizeDelay: 120,
    initRetryDelay: 300,
    initRetries: 10,

    transition: 'height 0.72s cubic-bezier(0.22, 1, 0.36, 1)'
  };

  var state = {
    activated: false,
    settleTimer: null,
    observer: null,
    lastSelectedSrc: '',
    lastAppliedHeight: null
  };

  function qs(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }

  function qsa(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }

  function getRoot() {
    return qs(SELECTORS.root);
  }

  function getWrapper() {
    var root = getRoot();
    return root ? qs(SELECTORS.wrapper, root) : null;
  }

  function getMediaList() {
    var root = getRoot();
    return root ? qs(SELECTORS.mediaList, root) : null;
  }

  function getSelectedSlide() {
    var root = getRoot();
    return root ? qs(SELECTORS.selected, root) : null;
  }

  function getSelectedImg() {
    var slide = getSelectedSlide();
    return slide ? slide.querySelector('img') : null;
  }

  function getAllImgs() {
    var root = getRoot();
    return root ? qsa(SELECTORS.imgs, root) : [];
  }

  function clearSettleTimer() {
    if (state.settleTimer) {
      clearTimeout(state.settleTimer);
      state.settleTimer = null;
    }
  }

  function isMockupImg(img) {
    if (!img) return false;
    var src = (img.getAttribute('src') || '').toLowerCase();
    var srcset = (img.getAttribute('srcset') || '').toLowerCase();
    var alt = (img.getAttribute('alt') || '').toLowerCase();
    return src.indexOf('mockup') !== -1 ||
           srcset.indexOf('mockup') !== -1 ||
           alt.indexOf('mockup') !== -1;
  }

  function getDefaultHeight() {
    return Math.round(
      Math.min(window.innerHeight * CONFIG.defaultVhFactor, window.innerWidth * CONFIG.defaultVwFactor)
    );
  }

  function getTargetHeight(img) {
    if (!img || !img.naturalWidth || !img.naturalHeight) {
      return getDefaultHeight();
    }

    var nw = img.naturalWidth;
    var nh = img.naturalHeight;
    var vw = window.innerWidth;
    var vh = window.innerHeight;

    if (isMockupImg(img)) {
      var mockupRatio = nh / nw;
      var mockupHeight = Math.min(vw * mockupRatio + CONFIG.mockupExtraPx, vh * CONFIG.mockupMaxVh);
      return Math.round(Math.max(mockupHeight, vh * CONFIG.mockupMinVh));
    }

    if (nw > nh) {
      var landscapeRatio = nh / nw;
      var landscapeHeight = Math.min(vw * landscapeRatio + CONFIG.landscapeExtraPx, vh * CONFIG.landscapeMaxVh);
      return Math.round(Math.max(landscapeHeight, vh * CONFIG.landscapeMinVh));
    }

    return getDefaultHeight();
  }

  function productHasLandscape() {
    var imgs = getAllImgs();
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      if (isMockupImg(img)) continue;
      if (!img.naturalWidth || !img.naturalHeight) continue;
      if (img.naturalWidth > img.naturalHeight) return true;
    }
    return false;
  }

  function applyHeightSmooth(height) {
    var wrapper = getWrapper();
    if (!wrapper || !height) return;
    if (state.lastAppliedHeight === height) return;
    wrapper.style.transition = CONFIG.transition;
    requestAnimationFrame(function () {
      wrapper.style.height = height + 'px';
      state.lastAppliedHeight = height;
    });
  }

  function updateForImage(img, delay) {
    if (!img) return;
    var run = function () {
      var targetHeight = getTargetHeight(img);
      clearSettleTimer();
      state.settleTimer = setTimeout(function () {
        applyHeightSmooth(targetHeight);
      }, delay);
    };
    if (img.complete && img.naturalWidth > 0) {
      run();
    } else {
      img.addEventListener('load', run, { once: true });
    }
  }

  function handleSelectedChange(customDelay) {
    var img = getSelectedImg();
    if (!img) return;
    var currentSrc = img.currentSrc || img.src || '';
    if (!currentSrc) return;
    if (currentSrc === state.lastSelectedSrc && customDelay !== CONFIG.resizeDelay) {
      return;
    }
    state.lastSelectedSrc = currentSrc;
    updateForImage(img, customDelay);
  }

  function bindObserver() {
    var mediaList = getMediaList();
    if (!mediaList) return;
    state.observer = new MutationObserver(function (mutations) {
      var foundSelectionChange = false;
      for (var i = 0; i < mutations.length; i++) {
        var mutation = mutations[i];
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'class' &&
          mutation.target &&
          mutation.target.classList &&
          mutation.target.classList.contains('is-selected')
        ) {
          foundSelectionChange = true;
          break;
        }
      }
      if (foundSelectionChange) {
        handleSelectedChange(CONFIG.changeDelay);
      }
    });
    state.observer.observe(mediaList, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });
  }

  function bindEvents() {
    document.addEventListener('variant:changed', function () {
      clearSettleTimer();
      setTimeout(function () {
        state.lastSelectedSrc = '';
        handleSelectedChange(CONFIG.variantDelay);
      }, 60);
    });

    var lastWidth = window.innerWidth;
    window.addEventListener('resize', function () {
      var newWidth = window.innerWidth;
      if (newWidth === lastWidth) return;
      lastWidth = newWidth;
      clearSettleTimer();
      state.lastSelectedSrc = '';
      handleSelectedChange(CONFIG.resizeDelay);
    }, { passive: true });
  }

  function activate() {
    if (state.activated) return;
    if (!productHasLandscape()) return;
    state.activated = true;
    var wrapper = getWrapper();
    if (wrapper) {
      wrapper.style.transition = CONFIG.transition;
    }
    bindObserver();
    bindEvents();
    setTimeout(function () {
      handleSelectedChange(180);
    }, 120);
  }

  function tryActivate(attemptsLeft) {
    if (state.activated) return;
    if (productHasLandscape()) {
      activate();
      return;
    }
    if (attemptsLeft > 0) {
      setTimeout(function () {
        tryActivate(attemptsLeft - 1);
      }, CONFIG.initRetryDelay);
    }
  }

  function init() {
    setTimeout(function () {
      tryActivate(CONFIG.initRetries);
    }, 150);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();









/* ================================================================
   NT – Product Media Turbo V9 (Final)
   ================================================================ */
(function () {
  'use strict';

  var ROOT = '.shopify-section--main-product';
  var ISEL = '.product__media-item[data-media-type="image"]';
  var ASEL = '.product__media-item[data-media-type="image"] img';
  var ZBTN = '.product__zoom-button';
  var MSEL = '.product__media-list';
  var SZ   = [200,300,400,500,600,700,800,1000,1200,1400,1600,1800,2000,2200,2400,2600,2800,3000];

  var G = window.__ntG = window.__ntG || {
    products : {},
    promises : {},
    hinted   : {},
    fetched  : {},
    pswpDone : false
  };

  var lazyObs  = null;
  var schedTmr = null;
  var varTmr   = null;
  var queue    = [];
  var qSet     = {};
  var running  = 0;

  var HAS_FETCH = typeof fetch === 'function';
  var HAS_MO    = typeof MutationObserver === 'function';
  var HAS_IDLE  = typeof requestIdleCallback === 'function';
  var HAS_ABORT = typeof AbortController === 'function';

  // [P2-A] Deferred Passive Preload Manager
  var _p2_passiveFired         = false;
  var _p2_passiveQueue         = [];
  var _p2_passiveTriggersBound = false;

  function _p2_enqueuePassive(fn) {
    if (_p2_passiveFired) {
      try { fn(); } catch (_) {}
      return;
    }
    _p2_passiveQueue.push(fn);
  }

  function _p2_firePassive() {
    if (_p2_passiveFired) return;
    _p2_passiveFired = true;
    var q = _p2_passiveQueue;
    _p2_passiveQueue = [];
    for (var i = 0; i < q.length; i++) {
      try { q[i](); } catch (_) {}
    }
  }

  function _p2_bindPassiveTriggers(r) {
    if (_p2_passiveTriggersBound) return;
    _p2_passiveTriggersBound = true;

    if (HAS_IDLE) {
      try { requestIdleCallback(_p2_firePassive, { timeout: 2500 }); } catch (_) {
        setTimeout(_p2_firePassive, 1500);
      }
    } else {
      setTimeout(_p2_firePassive, 1500);
    }

    var onIntent = function () { _p2_firePassive(); };
    try {
      r.addEventListener('pointerenter', onIntent, { passive: true, capture: true });
      r.addEventListener('pointerdown',  onIntent, { passive: true, capture: true });
      r.addEventListener('touchstart',   onIntent, { passive: true, capture: true });
    } catch (_) {}

    try {
      document.addEventListener('variant:changed', _p2_firePassive);
    } catch (_) {}
  }

  function _p2_reset() {
    _p2_passiveFired         = false;
    _p2_passiveQueue         = [];
    _p2_passiveTriggersBound = false;
  }

  function maxParallel() {
    try {
      var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (conn) {
        if (conn.saveData) return 2;
        var ect = conn.effectiveType || '';
        if (ect === 'slow-2g' || ect === '2g') return 2;
        if (ect === '3g') return 4;
      }
    } catch (_) {}
    return 6;
  }

  function safeQuery(root, sel) {
    if (!root || typeof root.querySelector !== 'function') return null;
    try { return root.querySelector(sel); } catch (_) { return null; }
  }

  function safeQueryAll(root, sel) {
    if (!root || typeof root.querySelectorAll !== 'function') return [];
    try { return Array.prototype.slice.call(root.querySelectorAll(sel)); } catch (_) { return []; }
  }

  function safeAttr(el, attr) {
    if (!el || typeof el.getAttribute !== 'function') return null;
    try { return el.getAttribute(attr); } catch (_) { return null; }
  }

  function safeClassList(el, cls) {
    if (!el || !el.classList) return false;
    try { return el.classList.contains(cls); } catch (_) { return false; }
  }

  function getRoot() { return safeQuery(document, ROOT); }

  function getHandle(r) {
    var el = safeQuery(r, 'product-media[product-handle]') ||
             safeQuery(r, 'product-image-zoom[product-handle]');
    return el ? safeAttr(el, 'product-handle') : null;
  }

  function maxZoom() {
    try {
      var v = window.themeVariables;
      return (v && v.settings && +v.settings.mobileZoomFactor) || 2;
    } catch (_) { return 2; }
  }

  function abs(url) {
    if (!url || typeof url !== 'string') return null;
    var u = url.trim();
    if (!u) return null;
    if (u.indexOf('//') === 0) return location.protocol + u;
    if (u.indexOf('http') === 0) return u;
    return null;
  }

  function normalizeUrl(url) {
    if (!url) return null;
    return url.replace(/^https?:/, '');
  }

  function sizedUrl(src, size) {
    if (!src || typeof src !== 'string') return null;
    var m = src.match(/\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif|webp)(\?v=\d+)?$/i);
    if (!m) return null;
    var parts = src.split(m[0]);
    if (parts.length < 2) return null;
    return (parts[0] + '_' + size + m[0]).replace(/^https?:/, '');
  }

  function zoomUrlFor(media) {
    try {
      if (!media || !media.preview_image) return null;
      var w = +media.preview_image.width || 0;
      if (!w) return null;
      var ss = [];
      for (var i = 0; i < SZ.length; i++) { if (w >= SZ[i]) ss.push(SZ[i]); }
      if (!ss.length) return null;
      var dpr  = Math.max(1, Math.min(+window.devicePixelRatio || 1, 3));
      var vw   = Math.max(320, window.innerWidth || 375);
      var desW = Math.min(ss[ss.length - 1], vw);
      var px   = Math.ceil(Math.min(desW * dpr * maxZoom(), 3000));
      var src  = (media.preview_image && media.preview_image.src) || media.url || null;
      return src ? sizedUrl(src, px + 'x') : null;
    } catch (_) { return null; }
  }

  function viewportUrl(img) {
    try {
      if (!img) return null;
      var ss = safeAttr(img, 'srcset') || img.srcset || '';
      if (ss) {
        var dpr     = Math.max(1, Math.min(+window.devicePixelRatio || 1, 3));
        var vw      = Math.max(320, window.innerWidth || 375);
        var targetW = Math.ceil(vw * dpr);
        var cands   = [];
        var parts   = ss.split(/,\s+(?=\S+\s+\d+w)/);
        if (parts.length < 2) parts = ss.split(',');
        for (var i = 0; i < parts.length; i++) {
          var p    = parts[i].trim().split(/\s+/);
          if (p.length < 2) continue;
          var wStr = p[p.length - 1];
          var wVal = 0;
          if (/^\d+w$/i.test(wStr))      wVal = parseInt(wStr, 10);
          else if (/^\d+x$/i.test(wStr)) wVal = Math.ceil(+wStr.replace('x','') * 400);
          if (p[0] && wVal > 0) cands.push({ url: p[0], w: wVal });
        }
        if (cands.length) {
          cands.sort(function (a, b) { return a.w - b.w; });
          for (var j = 0; j < cands.length; j++) {
            if (cands[j].w >= targetW) return cands[j].url;
          }
          return cands[cands.length - 1].url;
        }
      }
    } catch (_) {}
    try { return img.currentSrc || img.src || null; } catch (_) { return null; }
  }

  function hint(href) {
    if (!href || G.hinted[href]) return;
    G.hinted[href] = 1;
    try {
      var l = document.createElement('link');
      l.rel = 'preload';
      l.as = 'image';
      l.href = href;
      if ('fetchPriority' in l) l.fetchPriority = 'high';
      document.head.appendChild(l);
    } catch (_) {}
  }

  function enq(url, hi) {
    var href = abs(url);
    if (!href) return;
    var key = normalizeUrl(href);
    if (!key || G.fetched[key] || qSet[key]) return;
    qSet[key] = 1;
    if (hi) {
      queue.unshift(href);
      hint(href);
    } else {
      queue.push(href);
    }
    pump();
  }

  function pump() {
    var MAX_P = maxParallel();
    while (running < MAX_P && queue.length) {
      var href = queue.shift();
      var key  = normalizeUrl(href);
      if (!key || G.fetched[key]) continue;
      G.fetched[key] = 1;
      running++;
      (function (src) {
        var img = new Image();
        img.decoding = 'async';
        function done() {
          running = Math.max(0, running - 1);
          pump();
        }
        img.onload = done;
        img.onerror = done;
        try { img.src = src; } catch (_) { done(); }
      })(href);
    }
  }

  function fixImg(img) {
    if (!img || img.tagName !== 'IMG') return;
    try {
      if (safeAttr(img, 'loading') === 'lazy') {
        img.removeAttribute('loading');
        img.loading = 'eager';
      }
      img.decoding = 'async';
    } catch (_) {}
  }

  function forceEager(r) {
    var imgs = safeQueryAll(r, ASEL);
    for (var i = 0; i < imgs.length; i++) {
      fixImg(imgs[i]);
      try { if (i < 5) imgs[i].fetchPriority = 'high'; } catch (_) {}
    }
  }

  function watchLazy(r) {
    if (!HAS_MO) return;
    if (lazyObs) {
      try { lazyObs.disconnect(); } catch (_) {}
      lazyObs = null;
    }
    var scope = safeQuery(r, MSEL) || r;
    try {
      lazyObs = new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          var m = muts[i];
          try {
            if (m.type === 'attributes') {
              fixImg(m.target);
            } else if (m.type === 'childList') {
              var nodes = m.addedNodes;
              for (var n = 0; n < nodes.length; n++) {
                var nd = nodes[n];
                if (!nd || nd.nodeType !== 1) continue;
                if (nd.tagName === 'IMG') {
                  fixImg(nd);
                } else {
                  var ls = safeQueryAll(nd, 'img[loading="lazy"]');
                  for (var k = 0; k < ls.length; k++) fixImg(ls[k]);
                }
              }
            }
          } catch (_) {}
        }
      });
      lazyObs.observe(scope, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['loading']
      });
    } catch (_) {
      lazyObs = null;
    }
  }

  // [P2-B] preloadGallery mit deferPassive
  function preloadGallery(r, options) {
    options = options || {};
    var deferPassive = !!options.deferPassive;

    var allItems  = safeQueryAll(r, ISEL);
    var active    = safeQuery(r, ISEL + '.is-selected');
    var activeSet = [];
    var otherSet  = [];

    for (var i = 0; i < allItems.length; i++) {
      var item = allItems[i];
      if (safeClassList(item, 'is-filtered')) otherSet.push(item);
      else activeSet.push(item);
    }

    var aIdx = active ? activeSet.indexOf(active) : 0;
    if (aIdx < 0) aIdx = 0;

    var orderedActive  = [];
    var orderedPassive = [];
    var seen = {};

    function addA(idx) {
      if (idx < 0 || idx >= activeSet.length) return;
      var key = 'a' + idx;
      if (seen[key]) return;
      seen[key] = 1;
      orderedActive.push({ item: activeSet[idx], hi: Math.abs(idx - aIdx) <= 1 });
    }

    addA(aIdx);
    for (var off = 1; off < activeSet.length; off++) {
      addA(aIdx + off);
      addA(aIdx - off);
    }
    for (var o = 0; o < otherSet.length; o++) {
      orderedPassive.push({ item: otherSet[o], hi: false });
    }

    function enqueueList(list) {
      for (var e = 0; e < list.length; e++) {
        try {
          var img  = safeQuery(list[e].item, 'img');
          if (!img) continue;
          var best = viewportUrl(img);
          var cur  = null;
          try { cur = img.currentSrc || img.src || null; } catch (_) {}
          if (best) enq(best, list[e].hi);
          if (cur && cur !== best) enq(cur, list[e].hi);
        } catch (_) {}
      }
    }

    enqueueList(orderedActive);

    if (deferPassive) {
      _p2_enqueuePassive(function () {
        enqueueList(orderedPassive);
      });
    } else {
      enqueueList(orderedPassive);
    }
  }

  function primePhotoSwipe() {
    if (G.pswpDone) return;
    G.pswpDone = true;
    try {
      var src = window.themeVariables && window.themeVariables.libs && window.themeVariables.libs.photoswipe;
      if (src) {
        var href = abs(src);
        if (href && !G.hinted['s:' + href]) {
          G.hinted['s:' + href] = 1;
          var l = document.createElement('link');
          l.rel = 'preload';
          l.as = 'script';
          l.href = href;
          document.head.appendChild(l);
        }
      }
    } catch (_) {}
    try { LibraryLoader.load('photoswipe').catch(function () {}); } catch (_) {}
  }

  function doFetch(url, retries, timeoutMs) {
    if (!HAS_FETCH) {
      return new Promise(function (resolve) {
        try {
          var xhr = new XMLHttpRequest();
          xhr.open('GET', url, true);
          xhr.timeout = timeoutMs || 8000;
          xhr.onreadystatechange = function () {
            if (xhr.readyState !== 4) return;
            if (xhr.status >= 200 && xhr.status < 300) {
              try { resolve(JSON.parse(xhr.responseText)); }
              catch (_) { resolve(null); }
            } else {
              resolve(null);
            }
          };
          xhr.ontimeout = xhr.onerror = function () { resolve(null); };
          xhr.send();
        } catch (_) { resolve(null); }
      });
    }

    var controller = HAS_ABORT ? new AbortController() : null;
    var timer = controller
      ? setTimeout(function () { try { controller.abort(); } catch (_) {} }, timeoutMs || 8000)
      : null;

    var opts = { credentials: 'omit' };
    if (controller) opts.signal = controller.signal;

    return fetch(url, opts)
      .then(function (resp) {
        clearTimeout(timer);
        if (!resp.ok) return Promise.reject('HTTP ' + resp.status);
        return resp.json();
      })
      .then(function (data) {
        if (!data || typeof data !== 'object') return null;
        if (!Array.isArray(data.media)) data.media = [];
        return data;
      })
      .catch(function (err) {
        clearTimeout(timer);
        if (retries > 0 && !((err && err.name) === 'AbortError')) {
          return new Promise(function (res) { setTimeout(res, 1200); })
            .then(function () { return doFetch(url, retries - 1, timeoutMs); });
        }
        return null;
      });
  }

  function loadProduct(r) {
    var h = getHandle(r);
    if (!h || typeof h !== 'string' || !h.trim()) return Promise.resolve(null);
    if (G.products[h]) return Promise.resolve(G.products[h]);
    if (G.promises[h]) return G.promises[h];

    try {
      var loaded = window.ProductLoader && window.ProductLoader.loadedProducts && window.ProductLoader.loadedProducts[h];
      if (loaded && typeof loaded.then === 'function') {
        G.promises[h] = loaded.then(function (p) {
          if (p && typeof p === 'object') G.products[h] = p;
          return G.products[h] || null;
        });
        return G.promises[h];
      }
    } catch (_) {}

    var base = '';
    try { base = (window.themeVariables && window.themeVariables.routes && window.themeVariables.routes.rootUrlWithoutSlash) || ''; } catch (_) {}

    var safePath = h.replace(/[^a-zA-Z0-9_\-]/g, '');
    if (!safePath) return Promise.resolve(null);
    var url = base + '/products/' + safePath + '.js';

    G.promises[h] = doFetch(url, 2, 8000).then(function (p) {
      if (!p) return null;
      G.products[h] = p;
      try {
        if (window.ProductLoader && window.ProductLoader.loadedProducts) {
          window.ProductLoader.loadedProducts[h] = Promise.resolve(p);
        }
      } catch (_) {}
      return p;
    }).catch(function () { return null; });

    return G.promises[h];
  }

  function preloadZoomItems(r, product) {
    if (!product || !Array.isArray(product.media)) return;

    var activeIds = {};
    var items = safeQueryAll(r, ISEL + ':not(.is-filtered)');
    for (var i = 0; i < items.length; i++) {
      var id = parseInt(safeAttr(items[i], 'data-media-id'), 10);
      if (id) activeIds[id] = 1;
    }

    var activeUrls  = [];
    var passiveUrls = [];

    for (var j = 0; j < product.media.length; j++) {
      try {
        var m = product.media[j];
        if (!m || m.media_type !== 'image') continue;
        var url = zoomUrlFor(m);
        if (!url) continue;
        if (activeIds[m.id]) activeUrls.push(url);
        else passiveUrls.push(url);
      } catch (_) {}
    }

    for (var a = 0; a < activeUrls.length; a++) {
      enq(abs(activeUrls[a]), true);
    }

    function loadPassive() {
      for (var p = 0; p < passiveUrls.length; p++) {
        enq(abs(passiveUrls[p]), false);
      }
    }

    if (HAS_IDLE) {
      requestIdleCallback(loadPassive, { timeout: 3000 });
    } else {
      setTimeout(loadPassive, 500);
    }
  }

  function preloadZoom(r) {
    try {
      var h = getHandle(r);
      if (h && G.products[h]) {
        preloadZoomItems(r, G.products[h]);
        return;
      }
      loadProduct(r).then(function (p) {
        preloadZoomItems(r, p);
      }).catch(function () {});
    } catch (_) {}
  }

  // [P2-D] Nur aktives Zoom-Bild sofort preloaden
  function preloadActiveZoomOnly(r, product) {
    if (!product || !Array.isArray(product.media)) return;

    var active = safeQuery(r, ISEL + '.is-selected') || safeQuery(r, ISEL + ':not(.is-filtered)');
    if (!active) return;

    var id = parseInt(safeAttr(active, 'data-media-id'), 10);
    if (!id) return;

    for (var j = 0; j < product.media.length; j++) {
      try {
        var m = product.media[j];
        if (!m || m.id !== id || m.media_type !== 'image') continue;

        var url  = zoomUrlFor(m);
        var href = url ? abs(url) : null;
        if (!href) return;

        hint(href);

        var key = normalizeUrl(href);
        if (key && !G.fetched[key]) {
          G.fetched[key] = 1;
          var img = new Image();
          img.decoding = 'async';
          try { img.fetchPriority = 'high'; } catch (_) {}
          img.src = href;
        }
        return;
      } catch (_) {}
    }
  }

  // [P2-D] activeOnly-Option
  function instantZoomBoost(r, options) {
    options = options || {};
    var activeOnly = !!options.activeOnly;

    try {
      var h       = getHandle(r);
      var product = h ? G.products[h] : null;

      if (!product) {
        loadProduct(r).then(function (p) {
          if (!p) return;

          if (activeOnly) {
            preloadActiveZoomOnly(r, p);
          } else {
            preloadZoomItems(r, p);
          }
        }).catch(function () {});
        return;
      }

      if (activeOnly) {
        preloadActiveZoomOnly(r, product);
        return;
      }

      var active = safeQuery(r, ISEL + '.is-selected') || safeQuery(r, ISEL + ':not(.is-filtered)');
      if (!active) return;

      var id = parseInt(safeAttr(active, 'data-media-id'), 10);
      if (!id) return;

      for (var j = 0; j < product.media.length; j++) {
        try {
          var m = product.media[j];
          if (!m || m.id !== id || m.media_type !== 'image') continue;
          var url  = zoomUrlFor(m);
          var href = url ? abs(url) : null;
          if (!href) break;

          hint(href);

          var key = normalizeUrl(href);
          if (key && !G.fetched[key]) {
            G.fetched[key] = 1;
            var img = new Image();
            img.decoding = 'async';
            try { img.fetchPriority = 'high'; } catch (_) {}
            img.src = href;
          }
          break;
        } catch (_) {}
      }
    } catch (_) {}
  }

  function run(r, options) {
    try { forceEager(r); } catch (_) {}
    try { primePhotoSwipe(); } catch (_) {}
    try { preloadGallery(r, options); } catch (_) {}
    try { watchLazy(r); } catch (_) {}
  }

  function sched(r, ms) {
    clearTimeout(schedTmr);
    schedTmr = setTimeout(function () {
      try { run(r); } catch (_) {}
    }, ms);
  }

  function bind(r) {
    var zoomBoosted = false;

    document.addEventListener('variant:changed', function () {
      zoomBoosted = false;
      try {
        forceEager(r);
        preloadGallery(r);
      } catch (_) {}

      clearTimeout(varTmr);
      varTmr = setTimeout(function () {
        try {
          run(r);
          preloadZoom(r);
        } catch (_) {}
      }, 60);
    });

    if (HAS_MO) {
      var ml = safeQuery(r, MSEL);
      if (ml) {
        try {
          new MutationObserver(function () {
            sched(r, 40);
          }).observe(ml, { subtree: true, attributes: true, attributeFilter: ['class'] });
        } catch (_) {}
      }
    }

    function onZoomIntent(e) {
      try {
        if (!zoomBoosted && e.target && typeof e.target.closest === 'function' && e.target.closest(ZBTN)) {
          zoomBoosted = true;
          instantZoomBoost(r);
        }
      } catch (_) {}
    }

    r.addEventListener('pointerenter', function (e) {
      try {
        if (e.target && typeof e.target.closest === 'function' && e.target.closest(ZBTN)) {
          instantZoomBoost(r);
        }
      } catch (_) {}
    }, { passive: true, capture: true });

    r.addEventListener('pointerdown', onZoomIntent, { passive: true });
    r.addEventListener('touchstart', onZoomIntent, { passive: true });

    if (typeof document.hidden !== 'undefined') {
      document.addEventListener('visibilitychange', function () {
        if (!document.hidden) {
          try { pump(); } catch (_) {}
        }
      });
    }
  }

  function teardown() {
    clearTimeout(schedTmr);
    clearTimeout(varTmr);
    schedTmr = null;
    varTmr = null;

    if (lazyObs) {
      try { lazyObs.disconnect(); } catch (_) {}
      lazyObs = null;
    }

    queue = [];
    qSet = {};
    running = 0;
    _p2_reset();
  }

  function init() {
    var r = getRoot();
    if (!r || r.__ntV9) return;
    r.__ntV9 = true;

    _p2_bindPassiveTriggers(r);

    try { run(r, { deferPassive: true }); } catch (_) {}

    try { instantZoomBoost(r, { activeOnly: true }); } catch (_) {}

    _p2_enqueuePassive(function () {
      loadProduct(r).then(function () {
        try { preloadZoom(r); } catch (_) {}
      }).catch(function () {});
    });

    bind(r);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    teardown();
    var r = getRoot();
    if (r) r.__ntV9 = false;
    init();
  }, { passive: true });

  document.addEventListener('shopify:section:load', function () {
    teardown();
    var r = getRoot();
    if (r) r.__ntV9 = false;
    setTimeout(init, 50);
  });
})();


/* ================================================================
   NT – Thumbnail Eager Loader
   ================================================================ */
(function () {
  'use strict';

  var ROOT = '.shopify-section--main-product';
  var TSEL = '.product__thumbnail img';

  function run() {
    var r = document.querySelector(ROOT);
    if (!r) return;
    var imgs = r.querySelectorAll(TSEL);
    for (var i = 0; i < imgs.length; i++) {
      try {
        var img = imgs[i];
        if (img.getAttribute('loading') === 'lazy') {
          img.removeAttribute('loading');
          img.loading = 'eager';
        }
        var src = img.currentSrc || img.getAttribute('src');
        if (src) {
          var p = new Image();
          p.decoding = 'async';
          p.src = src;
        }
      } catch (_) {}
    }
  }

  function init() {
    run();

    document.addEventListener('variant:changed', function () {
      setTimeout(run, 30);
    });

    if (typeof MutationObserver === 'function') {
      var r = document.querySelector(ROOT);
      var thumbList = r && r.querySelector('.product__thumbnail-list-inner');
      if (thumbList) {
        new MutationObserver(function () {
          setTimeout(run, 20);
        }).observe(thumbList, { childList: true, subtree: true });
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function () {
    setTimeout(init, 60);
  });
})();




























/* ================================================================
   NT – Zoom Cart-Button Fix
   ================================================================ */
(function () {
  'use strict';

  var CART_SELECTORS = [
    '.product__sticky-bar',
    '.product-sticky-bar',
    '.shopify-section--main-product .product__info-sticky',
    '.product__info-sticky',
    '[data-section-type="main-product"] .product__cta',
    '.product__cta--sticky',
    'sticky-atc',
    'sticky-cart',
  ];

  var PSWP_SEL = '.pswp';

  var isZoomOpen   = false;
  var cartEls      = [];
  var pswpObserver = null;
  var bodyObserver = null;

  function findCartEls() {
    var found = [];
    for (var i = 0; i < CART_SELECTORS.length; i++) {
      try {
        var els = document.querySelectorAll(CART_SELECTORS[i]);
        for (var j = 0; j < els.length; j++) {
          if (found.indexOf(els[j]) === -1) found.push(els[j]);
        }
      } catch (_) {}
    }
    cartEls = found;
    return found;
  }

  function hideCart() {
    if (isZoomOpen) return;
    isZoomOpen = true;
    var els = cartEls.length ? cartEls : findCartEls();
    for (var i = 0; i < els.length; i++) {
      try {
        if (!els[i].__ntOrigVisibility) {
          els[i].__ntOrigVisibility  = els[i].style.visibility || '';
          els[i].__ntOrigOpacity     = els[i].style.opacity || '';
          els[i].__ntOrigPointer     = els[i].style.pointerEvents || '';
          els[i].__ntOrigZIndex      = els[i].style.zIndex || '';
        }
        els[i].style.visibility    = 'hidden';
        els[i].style.opacity       = '0';
        els[i].style.pointerEvents = 'none';
        els[i].style.zIndex        = '-1';
      } catch (_) {}
    }
  }

  function showCart() {
    if (!isZoomOpen) return;
    isZoomOpen = false;
    var els = cartEls.length ? cartEls : findCartEls();
    for (var i = 0; i < els.length; i++) {
      try {
        els[i].style.visibility    = els[i].__ntOrigVisibility || '';
        els[i].style.opacity       = els[i].__ntOrigOpacity || '';
        els[i].style.pointerEvents = els[i].__ntOrigPointer || '';
        els[i].style.zIndex        = els[i].__ntOrigZIndex || '';
      } catch (_) {}
    }
  }

  function bindThemeEvents() {
    var openEvents  = ['photoswipe:beforeOpen', 'photoswipe:open', 'lightbox:open', 'zoom:open'];
    var closeEvents = ['photoswipe:close', 'photoswipe:afterClose', 'lightbox:close', 'zoom:close'];
    openEvents.forEach(function (ev)  { document.addEventListener(ev, hideCart, { passive: true }); });
    closeEvents.forEach(function (ev) { document.addEventListener(ev, showCart, { passive: true }); });
  }

  function observePswp(pswpEl) {
    if (!pswpEl || pswpObserver || typeof MutationObserver !== 'function') return;
    try {
      pswpObserver = new MutationObserver(function () {
        var open = pswpEl.classList.contains('pswp--open');
        if (open && !isZoomOpen) hideCart();
        if (!open && isZoomOpen) showCart();
      });
      pswpObserver.observe(pswpEl, { attributes: true, attributeFilter: ['class'] });
    } catch (_) {}
  }

  function observeBody() {
    if (typeof MutationObserver !== 'function') return;
    try {
      bodyObserver = new MutationObserver(function () {
        var bodyOpen = document.body.classList.contains('pswp--open') ||
                       document.documentElement.classList.contains('pswp--open');

        var pswpEl   = document.querySelector(PSWP_SEL);
        var pswpOpen = pswpEl && pswpEl.classList.contains('pswp--open');

        if ((bodyOpen || pswpOpen) && !isZoomOpen) hideCart();
        if (!bodyOpen && !pswpOpen && isZoomOpen)  showCart();

        if (pswpEl && !pswpObserver) {
          observePswp(pswpEl);
          if (pswpObserver && bodyObserver) {
            try { bodyObserver.disconnect(); } catch (_) {}
            bodyObserver = null;
          }
        }
      });
      bodyObserver.observe(document.body, {
        subtree: true, attributes: true, attributeFilter: ['class']
      });
    } catch (_) {}
  }

  function injectCss() {
    try {
      var style = document.createElement('style');
      style.id  = 'nt-zoom-fix';
      style.textContent = [
        'body.pswp--open .product__sticky-bar,',
        'body.pswp--open .product-sticky-bar,',
        'body.pswp--open sticky-atc,',
        'body.pswp--open sticky-cart,',
        'html.pswp--open .product__sticky-bar,',
        'html.pswp--open .product-sticky-bar {',
        '  visibility: hidden !important;',
        '  opacity: 0 !important;',
        '  pointer-events: none !important;',
        '  z-index: -1 !important;',
        '}',
      ].join('\n');
      document.head.appendChild(style);
    } catch (_) {}
  }

  function init() {
    findCartEls();
    injectCss();
    bindThemeEvents();
    var pswpEl = document.querySelector(PSWP_SEL);
    if (pswpEl) observePswp(pswpEl);
    else observeBody();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function () { findCartEls(); });
})();


/* ================================================================
   NT – Mobile Tap-to-Zoom
   Auf Mobile (<=999px) ist der Zoom-Button per CSS versteckt.
   Ein Tap (kein Swipe) auf das aktive Bild öffnet den PhotoSwipe-Zoom
   über den unsichtbaren Zoom-Button – Theme-Logik bleibt intakt.
   Touch-Events statt staticClick: funktioniert auch bei 1 Slide.
   ================================================================ */
(function () {
  'use strict';

  var ROOT           = '.shopify-section--main-product';
  var WRAPPER_SEL    = '.product__media-list-wrapper';
  var ZOOM_BTN       = '.product__zoom-button';
  var DRAG_THRESHOLD = 10;

  function isMobile() {
    return window.innerWidth <= 999;
  }

  var root = document.querySelector(ROOT);
  if (!root) return;

  var _startX = 0, _startY = 0, _dragged = false;

  function onTouchStart(e) {
    var t = e.touches && e.touches[0];
    if (!t) return;
    _startX  = t.clientX;
    _startY  = t.clientY;
    _dragged = false;
  }

  function onTouchMove(e) {
    if (_dragged) return;
    var t = e.touches && e.touches[0];
    if (!t) return;
    if (
      Math.abs(t.clientX - _startX) > DRAG_THRESHOLD ||
      Math.abs(t.clientY - _startY) > DRAG_THRESHOLD
    ) {
      _dragged = true;
    }
  }

  function onTouchEnd(e) {
    if (!isMobile() || _dragged) return;

    var target = e.target;
    if (!target || typeof target.closest !== 'function') return;

    var mediaItem = target.closest('.product__media-item');
    if (!mediaItem) return;
    if (mediaItem.getAttribute('data-media-type') !== 'image') return;
    if (!mediaItem.classList.contains('is-selected')) return;
    if (mediaItem.classList.contains('is-filtered')) return;

    var zoomBtn = root.querySelector(ZOOM_BTN);
    if (!zoomBtn || zoomBtn.hasAttribute('hidden')) return;

    zoomBtn.click();
  }

  function bindWrapper() {
    var wrapper = root && root.querySelector(WRAPPER_SEL);
    if (!wrapper || wrapper.__ntTapZoom) return;
    wrapper.__ntTapZoom = true;
    wrapper.addEventListener('touchstart', onTouchStart, { passive: true });
    wrapper.addEventListener('touchmove',  onTouchMove,  { passive: true });
    wrapper.addEventListener('touchend',   onTouchEnd,   { passive: true });
  }

  bindWrapper();

  document.addEventListener('shopify:section:load', function () {
    var newRoot = document.querySelector(ROOT);
    if (newRoot) root = newRoot;
    bindWrapper();
  });

  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    var newRoot = document.querySelector(ROOT);
    if (newRoot) {
      root = newRoot;
      var w = root.querySelector(WRAPPER_SEL);
      if (w) w.__ntTapZoom = false;
    }
    bindWrapper();
  }, { passive: true });
})();














/* ================================================================
   NT – Focal Zoom Core Fix + Native Bounds Fix
   FINAL:
   - behält Focals nativen Zoom-Flow
   - korrigiert msrc auf das echte <img>
   - prewarmt die echte Zoom-src
   - gibt PhotoSwipe die exakten Bild-Bounds für Open + Close
   ================================================================ */
(function () {
  'use strict';

  var ROOT_SELECTOR = '.shopify-section--main-product';
  var patched = false;
  var preloadCache = new Map();

  function raf() {
    return new Promise(function (resolve) {
      requestAnimationFrame(resolve);
    });
  }

  async function frames(count) {
    for (var i = 0; i < count; i++) {
      await raf();
    }
  }

  function getMainRoot(node) {
    return (node && node.closest) ? node.closest(ROOT_SELECTOR) : document.querySelector(ROOT_SELECTOR);
  }

  function getMediaRoot(root) {
    return root ? root.querySelector('product-media') : null;
  }

  function getZoomEl(root) {
    var mediaRoot = getMediaRoot(root);
    return mediaRoot ? mediaRoot.querySelector('product-image-zoom') : null;
  }

  function getVisibleImageItems(mediaRoot) {
    if (!mediaRoot) return [];
    return Array.prototype.slice.call(
      mediaRoot.querySelectorAll('.product__media-item[data-media-type="image"]:not(.is-filtered)')
    );
  }

  function getSelectedVisibleItem(mediaRoot) {
    var items = getVisibleImageItems(mediaRoot);
    if (!items.length) return null;

    var selected = items.find(function (item) {
      return item.classList.contains('is-selected');
    });
    if (selected) return selected;

    return items[0];
  }

  function getItemByMediaId(mediaRoot, mediaId) {
    if (!mediaRoot || !mediaId) return null;
    return mediaRoot.querySelector(
      '.product__media-item[data-media-type="image"][data-media-id="' + mediaId + '"]:not(.is-filtered)'
    );
  }

  function getImgFromItem(item) {
    return item ? item.querySelector('.product__media-image-wrapper img, img') : null;
  }

  function getBoundsFromItem(item) {
    if (!item) return null;

    var target = getImgFromItem(item) || item;
    if (!target || typeof target.getBoundingClientRect !== 'function') return null;

    var rect = target.getBoundingClientRect();
    if (!rect || !rect.width || !rect.height) return null;

    var pageYScroll = window.pageYOffset || document.documentElement.scrollTop;

    return {
      x: rect.left,
      y: rect.top + pageYScroll,
      w: rect.width
    };
  }

  function preloadAndDecode(url) {
    return new Promise(function (resolve) {
      if (!url) return resolve();

      if (preloadCache.has(url)) {
        preloadCache.get(url).then(resolve).catch(resolve);
        return;
      }

      var promise = new Promise(function (done) {
        var img = new Image();
        var finished = false;

        function end() {
          if (finished) return;
          finished = true;
          done();
        }

        var timer = setTimeout(end, 3000);

        img.onload = function () {
          clearTimeout(timer);
          if (typeof img.decode === 'function') {
            img.decode().then(end).catch(end);
          } else {
            end();
          }
        };

        img.onerror = function () {
          clearTimeout(timer);
          end();
        };

        img.decoding = 'async';
        img.loading = 'eager';
        try { img.fetchPriority = 'high'; } catch (_) {}

        img.src = url;
      });

      preloadCache.set(url, promise);
      promise.then(resolve).catch(resolve);
    });
  }

  async function prewarmSelectedZoomSrc(root) {
    var zoomEl = getZoomEl(root);
    if (!zoomEl || typeof zoomEl._buildItems !== 'function') return;

    await frames(2);

    var items = await zoomEl._buildItems();
    if (!items || !items.length) return;

    var selectedIndex = items.findIndex(function (item) {
      return !!item.selected;
    });
    if (selectedIndex < 0) selectedIndex = 0;

    var selected = items[selectedIndex];
    var prev = items[selectedIndex - 1];
    var next = items[selectedIndex + 1];

    if (selected && selected.src) {
      preloadAndDecode(selected.src);
    }
    if (prev && prev.src) preloadAndDecode(prev.src);
    if (next && next.src) preloadAndDecode(next.src);
  }

  function patchBuildItems() {
    var ctor = window.customElements.get('product-image-zoom');
    if (!ctor) return false;

    var proto = ctor.prototype;
    if (!proto || typeof proto._buildItems !== 'function') return false;
    if (proto.__ntBuildItemsPatched) return true;

    proto._buildItems = async function () {
      var activeImages = Array.from(
        this.mediaElement.querySelectorAll('.product__media-item[data-media-type="image"]:not(.is-filtered)')
      );

      var product = await ProductLoader.load(this.getAttribute('product-handle'));
      var selectedItem = activeImages.find(function (item) {
        return item.classList.contains('is-selected');
      }) || activeImages[0] || null;

      return Promise.resolve(
        activeImages.map((item) => {
          var mediaId = parseInt(item.getAttribute('data-media-id'), 10);
          var matchedMedia = product.media.find(function (media) {
            return media.id === mediaId;
          });

          if (!matchedMedia) return null;

          var supportedSizes = getSupportedSizes(
            matchedMedia,
            [200, 300, 400, 500, 600, 700, 800, 1000, 1200, 1400, 1600, 1800, 2000, 2200, 2400, 2600, 2800, 3000]
          );

          var desiredWidth = Math.min(
            supportedSizes[supportedSizes.length - 1],
            window.innerWidth
          );

          var img = getImgFromItem(item);
          var msrc = img ? (img.currentSrc || img.src || '') : '';

          return {
            selected: item === selectedItem,
            src: getSizedMediaUrl(
              matchedMedia,
              '' + Math.ceil(Math.min(desiredWidth * window.devicePixelRatio * this.maxSpreadZoom, 3000)) + 'x'
            ),
            msrc: msrc,
            originalMedia: matchedMedia,
            alt: matchedMedia.alt ?? '',
            w: desiredWidth,
            h: parseInt(desiredWidth / matchedMedia.aspect_ratio, 10)
          };
        }).filter(Boolean)
      );
    };

    proto.__ntBuildItemsPatched = true;
    return true;
  }

  function patchOpenPhotoSwipeBounds() {
    var ctor = window.customElements.get('product-image-zoom');
    if (!ctor) return false;

    var proto = ctor.prototype;
    if (!proto || typeof proto._openPhotoSwipe !== 'function') return false;
    if (proto.__ntOpenPhotoSwipeBoundsPatched) return true;

    proto._openPhotoSwipe = async function () {
      var items = await this._buildItems();

      var selectedIndex = items.findIndex(function (item) {
        return item && item.selected;
      });
      if (selectedIndex < 0) selectedIndex = 0;

      var selectedItem = items[selectedIndex];
      var prevItem = items[selectedIndex - 1];
      var nextItem = items[selectedIndex + 1];

      if (selectedItem && selectedItem.src) preloadAndDecode(selectedItem.src);
      if (prevItem && prevItem.src) preloadAndDecode(prevItem.src);
      if (nextItem && nextItem.src) preloadAndDecode(nextItem.src);

      var resolveItemElement = (index) => {
        var mediaRoot = this.mediaElement;

        if (typeof index === 'number' && index >= 0 && index < items.length) {
          var pswpItem = items[index];
          var mediaId =
            pswpItem &&
            pswpItem.originalMedia &&
            pswpItem.originalMedia.id
              ? String(pswpItem.originalMedia.id)
              : null;

          if (mediaId) {
            var byId = getItemByMediaId(mediaRoot, mediaId);
            if (byId) return byId;
          }
        }

        return getSelectedVisibleItem(mediaRoot);
      };

      this.photoSwipeInstance = new window.ThemePhotoSwipe(this, PhotoSwipeUi, items, {
        index: selectedIndex,
        maxSpreadZoom: this.maxSpreadZoom,
        loop: false,
        allowPanToNext: false,
        closeOnScroll: false,
        closeOnVerticalDrag: MediaFeatures.supportsHover(),
        showHideOpacity: true,
        arrowKeys: true,
        history: false,
        getThumbBoundsFn: (index) => {
          var itemEl = resolveItemElement(index);
          var bounds = getBoundsFromItem(itemEl);

          if (bounds) return bounds;

          return {
            x: window.innerWidth / 2,
            y: (window.pageYOffset || document.documentElement.scrollTop) + (window.innerHeight / 2),
            w: 1
          };
        },
        getDoubleTapZoom: (isMouseClick, item) => {
          if (isMouseClick) {
            return item.w > item.h ? 1.6 : 1;
          } else {
            return item.initialZoomLevel < 0.7 ? 1 : 1.33;
          }
        }
      });

      var lastWidth = null;

      this.photoSwipeInstance.updateSize = new Proxy(this.photoSwipeInstance.updateSize, {
        apply: function (target, thisArg, argArray) {
          if (lastWidth !== window.innerWidth) {
            target.apply(thisArg, argArray);
            lastWidth = window.innerWidth;
          }
        }
      });

      this.photoSwipeInstance.listen('close', () => {
        this.open = false;
      });

      this.photoSwipeInstance.listen('afterChange', () => {
        setTimeout(() => {
          this.photoSwipeInstance.currItem.container.querySelector('img')?.setAttribute('alt', this.photoSwipeInstance.currItem.alt);
        }, 2000);
      });

      this.photoSwipeInstance.init();
    };

    proto.__ntOpenPhotoSwipeBoundsPatched = true;
    return true;
  }

  function bindPrewarm(root) {
    if (!root || root.__ntZoomCoreFixBound) return;
    root.__ntZoomCoreFixBound = true;

    var mediaRoot = getMediaRoot(root);
    if (!mediaRoot) return;

    var formId = mediaRoot.getAttribute('form-id');
    var form = formId ? document.getElementById(formId) : null;
    var zoomBtn = root.querySelector('.product__zoom-button');

    if (form && !form.__ntZoomVariantWarmBound) {
      form.__ntZoomVariantWarmBound = true;

      form.addEventListener('variant:change', function () {
        setTimeout(function () {
          prewarmSelectedZoomSrc(root);
        }, 80);

        setTimeout(function () {
          prewarmSelectedZoomSrc(root);
        }, 220);
      }, { passive: true });
    }

    if (!mediaRoot.__ntZoomSettleWarmBound) {
      mediaRoot.__ntZoomSettleWarmBound = true;

      mediaRoot.addEventListener('flickity:slide-settled', function () {
        prewarmSelectedZoomSrc(root);
      }, { passive: true });
    }

    if (zoomBtn && !zoomBtn.__ntZoomIntentWarmBound) {
      zoomBtn.__ntZoomIntentWarmBound = true;

      var warm = function () {
        prewarmSelectedZoomSrc(root);
      };

      zoomBtn.addEventListener('pointerenter', warm, { passive: true });
      zoomBtn.addEventListener('pointerdown', warm, { passive: true });
      zoomBtn.addEventListener('touchstart', warm, { passive: true });
    }
  }

  function boot() {
    var a = patchBuildItems();
    var b = patchOpenPhotoSwipeBounds();

    document.querySelectorAll(ROOT_SELECTOR).forEach(function (root) {
      bindPrewarm(root);
    });

    patched = !!(a && b);
    return patched;
  }

  async function init() {
    if (boot()) return;

    if (window.customElements && typeof window.customElements.whenDefined === 'function') {
      try {
        await window.customElements.whenDefined('product-image-zoom');
      } catch (_) {}
    }

    boot();

    var tries = 0;
    var timer = setInterval(function () {
      tries++;
      boot();
      if (patched || tries > 30) {
        clearInterval(timer);
      }
    }, 250);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function () {
    boot();
  });

  window.addEventListener('pageshow', function (e) {
    if (e.persisted) {
      boot();
    }
  }, { passive: true });
})();




/* =========================================================
   NT – PRODUCT IMAGE LOADING BAR (FINAL)
   - 250ms Grace Period
   - Sauberer Reset bei schnellen Variantenwechseln
   - decode() für präzise Ready-Erkennung
   ========================================================= */
(function () {
  'use strict';

  var bar = null;
  var fill = null;
  var graceTimer = null;
  var removeTimer = null;
  var currentImg = null;
  var GRACE_PERIOD = 250;

  function createBar() {
    var wrapper = document.querySelector('.product__media-list-wrapper');
    if (!wrapper) return null;

    var existing = document.getElementById('nt-image-progress');
    if (existing) {
      fill = document.getElementById('nt-image-progress-fill');
      return existing;
    }

    bar = document.createElement('div');
    bar.id = 'nt-image-progress';
    fill = document.createElement('div');
    fill.id = 'nt-image-progress-fill';
    bar.appendChild(fill);
    wrapper.appendChild(bar);
    return bar;
  }

  function getActiveImg() {
    var mediaList = document.querySelector('.product__media-list');
    if (!mediaList) return null;

    var item = mediaList.querySelector('.product__media-item.is-selected')
            || mediaList.querySelector('.product__media-item:not(.is-filtered)');
    return item ? item.querySelector('img') : null;
  }

  function hideBar() {
    if (!bar) return;
    bar.classList.remove('nt-bar--visible');
    bar.classList.add('nt-bar--complete');

    clearTimeout(removeTimer);
    removeTimer = setTimeout(function () {
      if (bar && bar.parentNode) {
        bar.parentNode.removeChild(bar);
        bar = null;
        fill = null;
      }
    }, 220);
  }

  function checkImageLoad() {
    var img = getActiveImg();
    if (!img || img === currentImg) return;
    currentImg = img;

    // Reset bei schnellem Durchklicken
    clearTimeout(graceTimer);
    clearTimeout(removeTimer);

    // Bild bereits im Cache? Komplett still bleiben.
    if (img.complete && img.naturalWidth > 0) {
      if (bar) hideBar();
      return;
    }

    // Grace Period: Balken erscheint nur, wenn's wirklich dauert
    graceTimer = setTimeout(function () {
      if (!img.complete || img.naturalWidth === 0) {
        if (!bar) bar = createBar();
        if (!bar) return;
        bar.classList.remove('nt-bar--complete');
        void bar.offsetWidth;
        bar.classList.add('nt-bar--visible');
      }
    }, GRACE_PERIOD);

    var onReady = function () {
      clearTimeout(graceTimer);
      if (bar) hideBar();
    };

    if (typeof img.decode === 'function') {
      img.decode().then(onReady).catch(onReady);
    } else {
      img.addEventListener('load', onReady, { once: true });
      img.addEventListener('error', onReady, { once: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', checkImageLoad);
  } else {
    checkImageLoad();
  }

  // Observer für Flickity-Swipes und Variantenwechsel
  var mo = new MutationObserver(function (mutations) {
    var slideChanged = false;
    for (var i = 0; i < mutations.length; i++) {
      var m = mutations[i];
      if (m.type === 'attributes' && m.attributeName === 'class' && m.target.classList.contains('is-selected')) {
        slideChanged = true;
        break;
      }
      if (m.type === 'childList' && m.addedNodes.length > 0) {
        slideChanged = true;
        break;
      }
    }
    if (slideChanged) {
      setTimeout(checkImageLoad, 10);
    }
  });

  var mediaList = document.querySelector('.product__media-list');
  if (mediaList) {
    mo.observe(mediaList, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
  }
})();





(() => {
  /* =========================================================
     NORDTABLO – COLLECTION DESKTOP HOVER = MOCKUP ONLY (OPTIMIZED)
     ========================================================= */

  'use strict';

  const SECTION_SELECTOR = '.shopify-section--main-collection';
  const CARD_SELECTOR = '.product-item';
  const LINK_SELECTOR = 'a[href*="/products/"]';
  const PRIMARY_IMG_SELECTOR = '.product-item__primary-image';
  const SECONDARY_IMG_SELECTOR = '.product-item__secondary-image';

  const DESKTOP_MQ = window.matchMedia('(min-width: 741px) and (hover: hover) and (pointer: fine)');
  const AGGRESSIVE_PRELOAD_MQ = window.matchMedia('(min-width: 1000px) and (hover: hover) and (pointer: fine)');
  const SHOPIFY_WIDTHS = [360, 533, 720, 940, 1066, 1200, 1440, 1600, 1800, 2000];

  const state = {
    observer: null,
    io: null,
    boundCards: new WeakSet(),
    productCache: new Map(),
    inflight: new Map(),
    preparedCards: new WeakSet(),
    preparingCards: new WeakSet()
  };

  function isDesktopHover() {
    return DESKTOP_MQ.matches;
  }

  function isAggressivePreloadRange() {
    return AGGRESSIVE_PRELOAD_MQ.matches;
  }

  function getSection() {
    return document.querySelector(SECTION_SELECTOR);
  }

  function extractHandleFromCard(card) {
    if (!card) return null;

    const link =
      card.querySelector(`${LINK_SELECTOR}.product-item__image-wrapper`) ||
      card.querySelector('.product-item__image-wrapper a[href*="/products/"]') ||
      card.querySelector(LINK_SELECTOR);

    if (!link) return null;

    const href = link.getAttribute('href') || '';
    const match = href.match(/\/products\/([^/?#]+)/i);
    return match ? match[1] : null;
  }

  function normalizeUrl(url) {
    if (!url) return null;
    if (url.startsWith('//')) return window.location.protocol + url;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return url;
  }

  function buildSizedUrl(src, width) {
    if (!src || !width) return src;

    const clean = src.split('?')[0];
    const match = clean.match(/\.(jpg|jpeg|png|webp|gif|bmp|tiff)(?:$)/i);
    if (!match) return src;

    const ext = match[0];
    const base = clean.slice(0, -ext.length);
    return `${base}_${width}x${ext}`;
  }

  function buildSrcset(src) {
    const abs = normalizeUrl(src);
    if (!abs) return '';

    return SHOPIFY_WIDTHS
      .map((w) => `${buildSizedUrl(abs, w)} ${w}w`)
      .join(', ');
  }

  async function fetchProduct(handle) {
    if (!handle) return null;

    if (state.productCache.has(handle)) {
      return state.productCache.get(handle);
    }

    if (state.inflight.has(handle)) {
      return state.inflight.get(handle);
    }

    const request = fetch(`/products/${handle}.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        state.productCache.set(handle, data);
        state.inflight.delete(handle);
        return data;
      })
      .catch((err) => {
        console.warn('[NT Mockup Hover] Produkt konnte nicht geladen werden:', handle, err);
        state.inflight.delete(handle);
        return null;
      });

    state.inflight.set(handle, request);
    return request;
  }

  function findMockupImage(product) {
    if (!product || !Array.isArray(product.images)) return null;

    const candidates = product.images.map(normalizeUrl).filter(Boolean);

    const exact =
      candidates.find((url) => /mockup1/i.test(url)) ||
      candidates.find((url) => /mockup/i.test(url));

    return exact || null;
  }

  function preloadImage(src) {
    return new Promise((resolve) => {
      if (!src) return resolve(false);

      const img = new Image();
      img.decoding = 'async';
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = src;
    });
  }

  function applyMockupToSecondary(card, mockupUrl) {
    if (!card || !mockupUrl) return;

    const secondary = card.querySelector(SECONDARY_IMG_SELECTOR);
    if (!secondary) return;

    if (!card.dataset.ntOriginalSecondarySrc) {
      card.dataset.ntOriginalSecondarySrc = secondary.getAttribute('src') || '';
    }
    if (!card.dataset.ntOriginalSecondarySrcset) {
      card.dataset.ntOriginalSecondarySrcset = secondary.getAttribute('srcset') || '';
    }
    if (!card.dataset.ntOriginalSecondarySizes) {
      card.dataset.ntOriginalSecondarySizes = secondary.getAttribute('sizes') || '';
    }

    secondary.setAttribute('src', buildSizedUrl(mockupUrl, 940));
    secondary.setAttribute('srcset', buildSrcset(mockupUrl));
    secondary.setAttribute(
      'sizes',
      '(min-width: 1400px) 22vw, (min-width: 1000px) 24vw, (min-width: 741px) 25vw, 100vw'
    );
    secondary.setAttribute('data-nt-mockup-ready', 'true');

    card.setAttribute('data-nt-mockup-hover', 'ready');
  }

  async function prepareCard(card) {
    if (!card || !isDesktopHover()) return;
    if (state.preparedCards.has(card) || state.preparingCards.has(card)) return;

    const secondary = card.querySelector(SECONDARY_IMG_SELECTOR);
    const primary = card.querySelector(PRIMARY_IMG_SELECTOR);
    if (!secondary || !primary) return;

    state.preparingCards.add(card);

    try {
      const handle = extractHandleFromCard(card);
      if (!handle) return;

      const product = await fetchProduct(handle);
      const mockupUrl = findMockupImage(product);
      if (!mockupUrl) return;

      const ok = await preloadImage(buildSizedUrl(mockupUrl, 940));
      if (!ok) return;

      applyMockupToSecondary(card, mockupUrl);
      state.preparedCards.add(card);
    } finally {
      state.preparingCards.delete(card);
    }
  }

  function schedulePrepare(card, delay = 0) {
    if (!card) return;

    if (delay <= 0) {
      prepareCard(card);
      return;
    }

    window.setTimeout(() => {
      prepareCard(card);
    }, delay);
  }

  function bindCard(card) {
    if (!card || state.boundCards.has(card)) return;
    state.boundCards.add(card);

    const imageArea =
      card.querySelector('.product-item__image-wrapper') ||
      card.querySelector('.product-item__aspect-ratio');

    if (!imageArea) return;

    imageArea.addEventListener('pointerenter', () => {
      schedulePrepare(card, 0);
    }, { passive: true });

    imageArea.addEventListener('mouseenter', () => {
      schedulePrepare(card, 0);
    }, { passive: true });

    imageArea.addEventListener('mousemove', () => {
      schedulePrepare(card, 0);
    }, { passive: true });

    imageArea.addEventListener('focusin', () => {
      schedulePrepare(card, 0);
    }, { passive: true });

    if (state.io) {
      state.io.observe(card);
    }
  }

  function scan() {
    const section = getSection();
    if (!section) return;

    section.querySelectorAll(CARD_SELECTOR).forEach(bindCard);
  }

  function setupIntersectionObserver() {
    if (state.io || !('IntersectionObserver' in window)) return;

    state.io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const card = entry.target;
        prepareCard(card);
        state.io.unobserve(card);
      });
    }, {
      root: null,
      rootMargin: '220px 0px',
      threshold: 0.01
    });
  }

  function setupMutationObserver() {
    const section = getSection();
    if (!section || state.observer) return;

    state.observer = new MutationObserver(() => {
      scan();
    });

    state.observer.observe(section, {
      childList: true,
      subtree: true
    });
  }

  function warmVisibleCardsSoon() {
    if (!isDesktopHover()) return;
    if (!isAggressivePreloadRange()) return;

    const section = getSection();
    if (!section) return;

    const cards = Array.from(section.querySelectorAll(CARD_SELECTOR)).slice(0, 8);
    cards.forEach((card, index) => {
      schedulePrepare(card, 80 * index);
    });
  }

  function init() {
    if (!isDesktopHover()) return;

    setupIntersectionObserver();
    setupMutationObserver();
    scan();
    warmVisibleCardsSoon();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', () => {
    scan();
    warmVisibleCardsSoon();
  });

  if (DESKTOP_MQ.addEventListener) {
    DESKTOP_MQ.addEventListener('change', () => {
      if (isDesktopHover()) {
        init();
      }
    });
  } else if (DESKTOP_MQ.addListener) {
    DESKTOP_MQ.addListener(() => {
      if (isDesktopHover()) {
        init();
      }
    });
  }

  if (AGGRESSIVE_PRELOAD_MQ.addEventListener) {
    AGGRESSIVE_PRELOAD_MQ.addEventListener('change', () => {
      if (isDesktopHover()) {
        warmVisibleCardsSoon();
      }
    });
  } else if (AGGRESSIVE_PRELOAD_MQ.addListener) {
    AGGRESSIVE_PRELOAD_MQ.addListener(() => {
      if (isDesktopHover()) {
        warmVisibleCardsSoon();
      }
    });
  }
})();


